import { inject } from '@angular/core';
import { Router, Routes, UrlTree } from '@angular/router';
import { homeRedirect } from './core/permissions/permission.service';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [homeRedirect],
    loadComponent: () =>
      import('./shared/components/loading-page/loading-page').then((m) => m.LoadingPage),
  },
  {
    path: 'auth',
    loadChildren: () => import('./features/auth/auth.routes').then((m) => m.AUTH_ROUTES),
  },
  {
    path: 'o',
    loadChildren: () =>
      import('./features/organization/organization.routes').then((m) => m.ORGANIZATION_ROUTES),
  },
  {
    path: 'root-console',
    loadChildren: () =>
      import('./features/root-console/root-console.routes').then((m) => m.ROOT_CONSOLE_ROUTES),
  },
  {
    path: 'dashboard',
    loadChildren: () =>
      import('./features/dashboard/dashboard.routes').then((m) => m.DASHBOARD_ROUTES),
  },
  {
    path: 'tables',
    loadChildren: () => import('./features/tables/tables.routes').then((m) => m.TABLES_ROUTES),
  },
  // Round 4, §3a — "operations" was a confusing route name for a screen the
  // rail, the <h1> and every e2e spec already called "Tables"; renamed, with
  // a redirect so an old bookmark still lands somewhere real. Floor and
  // Table requests used to be their own routes too, for the same reason.
  { path: 'operations', redirectTo: 'tables' },
  { path: 'floor', redirectTo: 'tables' },
  {
    path: 'requests',
    redirectTo: (): UrlTree => inject(Router).createUrlTree(['/tables'], { queryParams: { view: 'requests' } }),
  },
  // QA round 2, item II.10 — Guest Access moved from its own rail entry to a
  // Tables tab (a rarely-used screen doesn't earn a permanent rail slot);
  // redirected rather than removed so an old bookmark or a QR-generator
  // link still lands somewhere real, same reasoning as /floor and /requests
  // above.
  {
    path: 'access',
    redirectTo: (): UrlTree => inject(Router).createUrlTree(['/tables'], { queryParams: { view: 'access' } }),
  },
  {
    path: 'insights',
    loadChildren: () => import('./features/insights/insights.routes').then((m) => m.INSIGHTS_ROUTES),
  },
  {
    path: 'marketing',
    loadChildren: () => import('./features/marketing/marketing.routes').then((m) => m.MARKETING_ROUTES),
  },
  // Guests/Rewards used to be their own routes, then tabs inside Insights,
  // then moved again to Marketing (Round 7, §8). Redirected rather than
  // removed, same as /floor and /approvals above.
  {
    path: 'guests',
    redirectTo: (): UrlTree => inject(Router).createUrlTree(['/marketing'], { queryParams: { view: 'guests' } }),
  },
  {
    path: 'rewards',
    redirectTo: (): UrlTree => inject(Router).createUrlTree(['/marketing'], { queryParams: { view: 'rewards' } }),
  },
  {
    path: 'team',
    loadChildren: () => import('./features/team/team.routes').then((m) => m.TEAM_ROUTES),
  },
  // Employees/Roles/Attendance/Payroll used to be their own routes — now
  // tabs inside Team (Workspace Mock migration). Redirected rather than
  // removed, same as /floor and /approvals above.
  {
    path: 'employees',
    redirectTo: (): UrlTree => inject(Router).createUrlTree(['/team'], { queryParams: { view: 'employees' } }),
  },
  {
    path: 'roles',
    redirectTo: (): UrlTree => inject(Router).createUrlTree(['/team'], { queryParams: { view: 'roles' } }),
  },
  {
    path: 'tasks',
    loadChildren: () => import('./features/tasks/tasks.routes').then((m) => m.TASKS_ROUTES),
  },
  {
    path: 'attendance',
    redirectTo: (): UrlTree => inject(Router).createUrlTree(['/team'], { queryParams: { view: 'attendance' } }),
  },
  {
    path: 'inventory',
    loadChildren: () =>
      import('./features/inventory/inventory.routes').then((m) => m.INVENTORY_ROUTES),
  },
  {
    path: 'menu',
    loadChildren: () => import('./features/menu/menu.routes').then((m) => m.MENU_ROUTES),
  },
  {
    path: 'orders',
    loadChildren: () => import('./features/orders/orders.routes').then((m) => m.ORDERS_ROUTES),
  },
  {
    path: 'billing',
    loadChildren: () => import('./features/billing/billing.routes').then((m) => m.BILLING_ROUTES),
  },
  // Approvals used to be its own route — now a tab inside Tasks (Workspace
  // Mock migration). Redirected rather than removed, same as /floor and
  // /requests above, so an old bookmark still lands somewhere real.
  {
    path: 'approvals',
    redirectTo: (): UrlTree => inject(Router).createUrlTree(['/tasks'], { queryParams: { view: 'approvals' } }),
  },
  // Expenses and Purchases merged into one hub, /spend: every purchase
  // auto-posts an expense row (functions/src/domains/purchases/service.ts),
  // so the two were never really separate screens — putting them beside
  // each other as tabs is what actually shows the relationship, after
  // trying and failing to say it with names alone ("Purchases" vs
  // "Overheads": "overheads" means fixed costs, and this ledger also holds
  // advances, staff costs and the purchase rows themselves).
  //
  // Both old routes redirect rather than disappear — the house "never
  // delete a capability, redirect it" rule — carrying `?view=` through so a
  // deep link to a specific tab still lands on that tab, not just the hub.
  {
    path: 'expenses',
    // redirectTo functions receive the matched route's own snapshot data
    // (queryParams included) rather than needing an injected ActivatedRoute
    // — see @angular/router's RedirectFunction type.
    redirectTo: ({ queryParams }): UrlTree =>
      inject(Router).createUrlTree(['/spend'], { queryParams: { view: queryParams['view'] ?? 'expenses' } }),
  },
  {
    path: 'purchases',
    redirectTo: (): UrlTree => inject(Router).createUrlTree(['/spend'], { queryParams: { view: 'purchases' } }),
  },
  {
    path: 'spend',
    loadChildren: () => import('./features/spend/spend.routes').then((m) => m.SPEND_ROUTES),
  },
  {
    path: 'payroll',
    redirectTo: (): UrlTree => inject(Router).createUrlTree(['/team'], { queryParams: { view: 'payroll' } }),
  },
  {
    path: 'reports',
    redirectTo: (): UrlTree => inject(Router).createUrlTree(['/insights'], { queryParams: { view: 'revenue' } }),
  },
  {
    path: 'ai',
    loadChildren: () => import('./features/ai-concierge/ai-concierge.routes').then((m) => m.AI_CONCIERGE_ROUTES),
  },
  // Chef & Waiter phone screens (PIN sign-in) — chrome-free, see
  // features/staff-floor/staff-floor.routes.ts and shell.ts showShell().
  {
    path: 'staff',
    loadChildren: () => import('./features/staff-floor/staff-floor.routes').then((m) => m.STAFF_SIGNIN_ROUTES),
  },
  {
    path: 'kitchen',
    loadChildren: () => import('./features/staff-floor/staff-floor.routes').then((m) => m.KITCHEN_ROUTES),
  },
  {
    path: 'waiter',
    loadChildren: () => import('./features/staff-floor/staff-floor.routes').then((m) => m.WAITER_ROUTES),
  },
  {
    // Scanned from a table's QR code — no auth guard, see guest.routes.ts.
    path: 'guest',
    loadChildren: () => import('./features/guest/guest.routes').then((m) => m.GUEST_ROUTES),
  },
  {
    // The canonical Smart QR URL is `${origin}/q/{token}` (buildGuestScanUrl,
    // docs/prod-setup.md, every printed sticker) — but the gateway component
    // lives under GUEST_ROUTES at `guest/q/:token`. Without this top-level
    // entry the router matches nothing and every scanned QR dead-ends on a
    // rewrite to index.html. Redirect (param-preserving) rather than a second
    // component mount so the gateway route stays single-sourced.
    path: 'q/:token',
    redirectTo: 'guest/q/:token',
  },
  {
    path: 'settings',
    loadChildren: () =>
      import('./features/settings/settings.routes').then((m) => m.SETTINGS_ROUTES),
  },
  {
    path: 'no-access',
    loadComponent: () =>
      import('./shared/components/no-access/no-access').then((m) => m.NoAccess),
  },
];
